// Lib Imports
import React, { useEffect, useContext } from 'react';
import { Avatar, Button, Box, Text, DropButton, ThemeContext } from 'grommet';
import { CheckboxSelected, MoreVertical, Hide, View } from 'grommet-icons';
import styled from 'styled-components';
import { Bell } from '@styled-icons/fa-solid/Bell';
import PropTypes from 'prop-types';

// Core Imports
import Tooltip from 'granite-admin/core/components/Tooltip';
import Loader from 'granite-admin/core/components/Loader';
import NoRecordsPlaceholder from 'granite-admin/core/components/NoRecordsPlaceholder';
import NotificationSkeletonLoader from './NotificationSkeletonLoader';
const StyledText = styled(Text)`
  a {
    color: #0e84ad;
    text-decoration: underline;
  }
  a:hover {
    color: #fbab31;
  }
`;
const AvatarBox = styled(Avatar)`
  position: relative;
`;
const AvatarBell = styled(Bell)`
  color: ${({
  theme,
  background
}) => theme.global.colors[background]};
`;
const StatusIcon = styled(Box)`
  position: absolute;
  top: 4px;
  right: 12px;
`;
const NotificationStack = ({
  notifications,
  loader,
  handleNotificationClick,
  page,
  handleFetchNotifications,
  hoverIndicatorOptions,
  markAllRead,
  showUnread,
  isRead,
  open,
  setOpen,
  showSkeletonLoader
}) => {
  const theme = useContext(ThemeContext);
  const {
    headingColor = 'white',
    moreVerticalColor = 'white'
  } = theme?.notification || {};
  useEffect(() => {
    document.getElementsByClassName('notification_box')[0].addEventListener('click', () => {
      setOpen(false);
    });
  }, [setOpen]);
  const handleScroll = e => {
    const before = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight;
    if (before && page && !loader) {
      if (isRead === 'FETCH_UNREAD_ONCE') handleFetchNotifications('FETCH_UNREAD_ONSCROLL');else handleFetchNotifications();
    }
  };
  const itemsRenderer = () => {
    return /*#__PURE__*/React.createElement(Box, {
      pad: "small",
      gap: "medium"
    }, /*#__PURE__*/React.createElement(Button, {
      plain: true,
      onClick: markAllRead,
      icon: /*#__PURE__*/React.createElement(CheckboxSelected, {
        size: "small",
        color: "#2b2828"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        size: "14px"
      }, " Mark All Read"),
      gap: "10px"
    }), /*#__PURE__*/React.createElement(Button, {
      plain: true,
      onClick: isRead !== 'FETCH_UNREAD_ONCE' ? showUnread : () => showUnread('FETCH_ALL'),
      icon: isRead !== 'FETCH_UNREAD_ONCE' ? /*#__PURE__*/React.createElement(Hide, {
        size: "23px",
        color: "#615f5f"
      }) : /*#__PURE__*/React.createElement(View, {
        size: "17px",
        color: "#615f5f"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        size: "14px"
      }, isRead !== 'FETCH_UNREAD_ONCE' ? 'Show Unread' : 'Show All'),
      alignSelf: "start"
    }));
  };
  return /*#__PURE__*/React.createElement(Box, {
    background: "sidebar-drops",
    width: "328px",
    className: "notification_box"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: {
      shrink: 0
    },
    direction: "row",
    background: "brand",
    pad: "12px",
    align: "center",
    justify: "between",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "large",
    weight: "bold",
    color: headingColor
  }, "Notifications"), /*#__PURE__*/React.createElement(DropButton, {
    icon: /*#__PURE__*/React.createElement(MoreVertical, {
      size: "small",
      color: moreVerticalColor,
      onClick: () => setOpen(!open)
    }),
    dropAlign: {
      top: 'bottom',
      right: 'right'
    },
    dropProps: {
      round: 'small',
      elevation: 'medium',
      background: 'white',
      pad: 'small'
    },
    dropContent: itemsRenderer(),
    open: open
  })), /*#__PURE__*/React.createElement(Box, {
    flex: {
      grow: 1
    },
    height: "352px",
    overflow: {
      vertical: 'auto'
    },
    onScroll: handleScroll
  }, notifications?.length > 0 ? notifications.map((notification, index) => /*#__PURE__*/React.createElement(Box, {
    key: index,
    direction: "row-responsive",
    align: "center",
    onClick: () => handleNotificationClick(notification),
    pad: {
      vertical: 'small',
      left: 'small',
      right: 'medium'
    },
    hoverIndicator: hoverIndicatorOptions,
    flex: {
      shrink: 0
    },
    focusIndicator: false
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    gap: "small",
    align: "center"
  }, getUserAvatar(notification.status), /*#__PURE__*/React.createElement(Box, {
    width: "medium"
  }, /*#__PURE__*/React.createElement(StyledText, {
    weight: "bold",
    dangerouslySetInnerHTML: {
      __html: notification.text
    }
  }), /*#__PURE__*/React.createElement(Text, {
    color: "status-disabled"
  }, /*#__PURE__*/React.createElement(Tooltip, {
    content: notification.createdAt,
    align: "bottom"
  }, /*#__PURE__*/React.createElement(Text, null, notification.createdAt))))))) : !loader && /*#__PURE__*/React.createElement(Box, {
    elevation: "small",
    round: "small",
    fill: true,
    pad: "small"
  }, /*#__PURE__*/React.createElement(NoRecordsPlaceholder, {
    msg: "No Notification Found"
  })), loader && showSkeletonLoader ? /*#__PURE__*/React.createElement(NotificationSkeletonLoader, null) : loader && /*#__PURE__*/React.createElement(Box, {
    flex: {
      shrink: 0
    },
    width: "100%",
    margin: {
      vertical: 'medium'
    },
    align: "center",
    justify: "center"
  }, /*#__PURE__*/React.createElement(Loader, {
    size: "20px"
  }))));
};
const getUserAvatar = status => {
  return /*#__PURE__*/React.createElement(AvatarBox, {
    flex: "grow"
  }, /*#__PURE__*/React.createElement(Avatar, {
    size: "34px",
    background: "notification-avatar-background",
    round: "full"
  }, /*#__PURE__*/React.createElement(AvatarBell, {
    size: "18px",
    background: "notification-avatar-color"
  })), !status && /*#__PURE__*/React.createElement(StatusIcon, {
    flex: {
      shrink: 0
    },
    round: "full",
    background: "notification-avatar-color",
    height: "10px",
    width: "10px"
  }));
};
NotificationStack.propTypes = {
  notifications: PropTypes.array,
  loader: PropTypes.bool,
  handleNotificationClick: PropTypes.func,
  page: PropTypes.number,
  handleFetchNotifications: PropTypes.func,
  markAllRead: PropTypes.func,
  hoverIndicatorOptions: PropTypes.object,
  isRead: PropTypes.string,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  showUnread: PropTypes.func,
  showSkeletonLoader: PropTypes.bool
};
export default NotificationStack;